import React from "react";
import {
    Box, Typography, Button, Card, CardContent, IconButton, Chip,
    TextField, FormControl, InputLabel, Select, MenuItem,
} from "@mui/material";
import { Delete as DeleteIcon, Add as AddIcon } from "@mui/icons-material";
import { CustomerAddress } from "../../types";

export type EditableAddress = Omit<
    CustomerAddress,
    "address_id" | "customer_id" | "create_time" | "update_time"
>;

interface CustomerAddressEditorProps {
    addresses: EditableAddress[];
    onChange: (addresses: EditableAddress[]) => void;
}

/**
 * Add/edit/remove/default-select for a customer's local, not-yet-saved
 * address list. Shared by CustomerForm (admin create/edit) and POS's
 * CustomerCreationDialog so the "exactly one default per address_type"
 * rule — the same invariant AddressService.set_default_address enforces
 * server-side once an address is persisted — lives in one place instead of
 * being reimplemented per form.
 */
export const CustomerAddressEditor: React.FC<CustomerAddressEditorProps> = ({ addresses, onChange }) => {
    const addAddress = () => {
        onChange([
            ...addresses,
            {
                address_type: "SHIPPING",
                street_address: "",
                city: "",
                map_link: "",
                is_default: addresses.length === 0,
            },
        ]);
    };

    const removeAddress = (index: number) => {
        const removed = addresses[index];
        const rest = addresses.filter((_, i) => i !== index);
        // Removing the default of a type promotes the next address of that
        // same type, so a type never silently ends up with zero defaults.
        if (removed?.is_default) {
            const nextOfType = rest.find((a) => a.address_type === removed.address_type);
            if (nextOfType) nextOfType.is_default = true;
        }
        onChange(rest);
    };

    const updateAddress = (index: number, field: keyof EditableAddress, value: string) => {
        let updated = addresses.map((a, i) => (i === index ? { ...a, [field]: value } : a));
        if (field === "address_type") {
            // Retyping a default address (e.g. SHIPPING -> BILLING) can
            // collide with an existing default of the new type — demote
            // this one rather than leaving two defaults of the same type.
            const stillDefault = updated[index].is_default;
            const collides = updated.some((a, i) => i !== index && a.address_type === value && a.is_default);
            if (stillDefault && collides) {
                updated = updated.map((a, i) => (i === index ? { ...a, is_default: false } : a));
            }
        }
        onChange(updated);
    };

    const setDefault = (index: number) => {
        const type = addresses[index].address_type;
        onChange(
            addresses.map((a, i) => ({
                ...a,
                is_default: i === index ? true : a.address_type === type ? false : a.is_default,
            })),
        );
    };

    return (
        <Box sx={{ mt: 4 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                <Typography variant="h6">Addresses</Typography>
                <Button startIcon={<AddIcon />} onClick={addAddress} variant="outlined" size="small">
                    Add Address
                </Button>
            </Box>

            {addresses.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    No addresses added yet. Click "Add Address" to add customer addresses.
                </Typography>
            ) : (
                addresses.map((address, index) => (
                    <Card key={index} variant="outlined" sx={{ mb: 2 }}>
                        <CardContent>
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                    <Typography variant="subtitle1">Address {index + 1}</Typography>
                                    {address.is_default ? (
                                        <Chip label="Default" color="primary" size="small" />
                                    ) : (
                                        <Button size="small" onClick={() => setDefault(index)}>
                                            Set as Default
                                        </Button>
                                    )}
                                </Box>
                                <IconButton size="small" onClick={() => removeAddress(index)} color="error">
                                    <DeleteIcon />
                                </IconButton>
                            </Box>

                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 2 }}>
                                <Box sx={{ flex: "1 1 200px" }}>
                                    <FormControl fullWidth margin="normal">
                                        <InputLabel>Address Type</InputLabel>
                                        <Select
                                            value={address.address_type}
                                            label="Address Type"
                                            onChange={(e) => updateAddress(index, "address_type", e.target.value)}
                                        >
                                            <MenuItem value="SHIPPING">Shipping</MenuItem>
                                            <MenuItem value="BILLING">Billing</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Box>
                            </Box>

                            <TextField
                                fullWidth
                                margin="normal"
                                label="Street Address"
                                value={address.street_address}
                                onChange={(e) => updateAddress(index, "street_address", e.target.value)}
                                required
                            />

                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mt: 2 }}>
                                <Box sx={{ flex: "1 1 200px" }}>
                                    <TextField
                                        fullWidth
                                        margin="normal"
                                        label="City"
                                        value={address.city}
                                        onChange={(e) => updateAddress(index, "city", e.target.value)}
                                        required
                                    />
                                </Box>
                            </Box>

                            <TextField
                                fullWidth
                                margin="normal"
                                label="Map Link (Optional)"
                                value={address.map_link || ""}
                                onChange={(e) => updateAddress(index, "map_link", e.target.value)}
                                placeholder="https://maps.google.com/..."
                            />
                        </CardContent>
                    </Card>
                ))
            )}
        </Box>
    );
};
